import { getAnonymousLdUser, getSignedInLdUser } from "@src/appV2/FeatureFlags/cbhUserContext";
import { SessionType } from "@src/modules/interface";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";

interface InitializeLaunchDarklyProps {
  profile?: SessionType["session"]["profile"];
  isAdmin: boolean;
  adminUserId?: string;
  workplaceId?: string;
}

interface InitializeLaunchDarklyResult {
  isIdentifiedForCurrentUser: boolean;
}

// Sentinel for LD's anonymous user (which has no `key`), so we can store and
// compare "which user LD is currently identified as" in a single string.
const ANONYMOUS_LD_USER_KEY = "__anonymous__";

// `ldClient.identify()` resolves once flags for the new context arrive. In
// streaming mode it can occasionally never settle (the SSE connection stays
// open without delivering the initial payload for a given context), which would
// leave the auth gate that consumes `isIdentifiedForCurrentUser` spinning
// forever (ORB-2198). Treat identify as best-effort: proceed after this
// deadline so the app always renders — LD keeps syncing flags in the
// background and re-renders consumers when they arrive. Normal identify
// completes well under a second, so healthy sessions never hit this.
const LD_IDENTIFY_TIMEOUT_MS = 5_000;

export function useInitializeLaunchDarkly(
  props: InitializeLaunchDarklyProps
): InitializeLaunchDarklyResult {
  const { profile, isAdmin, adminUserId, workplaceId } = props;
  const ldClient = useLDClient();
  const [identifiedKey, setIdentifiedKey] = useState<string | undefined>(undefined);

  // The key LD must be identified as for this render to be trustworthy.
  // During session hydration, `profile.userId` flips from undefined → a real
  // id; if we only tracked a boolean, a previously-completed anonymous
  // identify would look "done" while the component is already rendering for
  // the signed-in user, and flag-dependent redirects would evaluate against
  // anonymous defaults. Comparing the identified key to the expected key
  // closes that window — this only passes when LD is identified as the user
  // currently in props.
  const expectedKey = profile?.userId ?? ANONYMOUS_LD_USER_KEY;
  const isIdentifiedForCurrentUser = identifiedKey === expectedKey;

  // Failsafe: never let `isIdentifiedForCurrentUser` stay false indefinitely.
  // The identify effect below normally flips it within a second, but if that
  // path stalls for any reason — a never-settling `identify()`, a missing
  // `ldClient`, an error before the `finally` — the auth gate that consumes
  // this would spin forever (ORB-2198). After the deadline, treat the current
  // user as identified so the app renders; LD keeps syncing flags in the
  // background. Only arm the timer while identify is still pending: once it
  // settles (identifiedKey === expectedKey) this re-runs and clears the timer.
  useEffect(() => {
    if (identifiedKey === expectedKey) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setIdentifiedKey(expectedKey);
    }, LD_IDENTIFY_TIMEOUT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [expectedKey, identifiedKey]);

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    let cancelled = false;

    void (async () => {
      const ldUser = !profile?.userId
        ? getAnonymousLdUser()
        : getSignedInLdUser({
            userId: profile?.userId ?? "",
            name: profile?.name ?? "",
            email: profile?.email ?? "",
            msa:
              profile?.fullAddress?.metropolitanStatisticalArea ??
              profile?.address?.metropolitanStatisticalArea ??
              "",
            pricingMsa: profile?.pricingMsa ?? "",
            city: profile?.fullAddress?.city ?? "",
            state: profile?.fullAddress?.state ?? "",
            stateCode: profile?.fullAddress?.stateCode ?? "",
            type: profile?.type ?? "",
            hcfWebVersion: __APP_VERSION__,
            userType: !isAdmin ? "WORKPLACE_USER" : "CBH_EMPLOYEE",
            adminUserId,
            parentFacilityId: profile?.parentFacilityId,
            workplaceId,
          });
      const nextKey = profile?.userId ?? ANONYMOUS_LD_USER_KEY;

      try {
        await ldClient.identify(ldUser);
      } catch {
        // LaunchDarkly client may not be fully initialized yet, ignore the error
        // The client will eventually sync when ready
      } finally {
        // Ignore stale identify() completions when deps have changed mid-await
        // (e.g. profile.userId swapped), so the last-to-resolve promise can't
        // overwrite a later, correct identify.
        if (!cancelled) {
          setIdentifiedKey(nextKey);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ldClient,
    profile?.userId,
    profile?.name,
    profile?.email,
    profile?.fullAddress?.metropolitanStatisticalArea,
    profile?.pricingMsa,
    profile?.fullAddress?.city,
    profile?.fullAddress?.state,
    profile?.fullAddress?.stateCode,
    profile?.type,
    profile?.parentFacilityId,
    isAdmin,
    adminUserId,
    workplaceId,
  ]);

  return { isIdentifiedForCurrentUser };
}
